import { ACCEPTED_METHODS, BlikPopup, CardPopup, RecurringConsent, getMethodDescription, getPaymentIcon, isNavigatingResult, isWalletRedirect, useAvailableMethods } from 'payments'
import { useHistory, useLocation } from 'react-router-dom'
import usePaymentProcessing from './hooks/usePaymentProcessing'
import usePaymentMethods from './hooks/usePaymentMethods'
import { useState, useEffect, useRef } from 'react'
import ArrowBtn from 'components/buttons/ArrowBtn'
import useTranslation from 'hooks/useTranslation'
import BackBtn from 'components/buttons/BackBtn'
import usePopupState from 'hooks/usePopupState'
import { displayPrice } from 'utils/string'
import Loading from 'components/Loading'
import Option from 'components/Option'
import Error from 'components/Error'
import Copy from 'components/Copy'
import { v7 } from 'uuid'
import 'payments/style.scss'

const Payment = ({ userId = '', returnPath = '', getInvoices, additionalElement = <></>, invoiceIds = undefined, grossPrice, secondaryTheme = true, summaryTop = '', summaryBottom = '', paymentMethods: _paymentMethods = undefined, close }) => {
  const { popup } = usePopupState()
  const [warning, setWarning] = useState(false)
  const [selected, select] = useState({ methodId: '' })
  const { isLoading, paymentMethods: allPaymentMethods } = usePaymentMethods(invoiceIds, _paymentMethods, ACCEPTED_METHODS)
  const paymentMethods = useAvailableMethods(allPaymentMethods)
  const { isProcessing, bankTransfer, purchase, generateBody, handleResult, clearBankTransfer } = usePaymentProcessing({
    selected,
    getInvoices,
    amount: grossPrice,
    paymentMethods: allPaymentMethods,
    userId,
    returnPath,
    close
  })
  const bottomRef = useRef(null)

  /**
   * Wynik z popupu BLIK / karty czeka, az wpis popupu zniknie z historii.
   * Popup zamyka sie przez goBack(), a close() kontenera zwykle tez robi goBack().
   * Dwa back() w jednym tiku Chrome wykona oba, a Safari scali je w jeden:
   * klient zostawal wtedy na ekranie platnosci. Dlatego close() dopiero po popstate.
   */
  const { goBack } = useHistory()
  const { state: locationState } = useLocation()
  const isPopupInHistory = !!locationState?.popup
  const deferredResult = useRef(null)
  const deferredTimeout = useRef(null)

  const flushDeferredResult = () => {
    clearTimeout(deferredTimeout.current)
    if (!deferredResult.current) return
    const { result, localPaymentId } = deferredResult.current
    deferredResult.current = null
    handleResult(result, localPaymentId)
  }

  const onPopupResult = (result, localPaymentId) => {
    // anulowanie (brak wyniku) i przekierowanie do bramki - bez czekania
    if (!result || result.type === 'redirect') {
      handleResult(result, localPaymentId)
      return
    }
    deferredResult.current = { result, localPaymentId }
    // asekuracja, gdyby popstate nie nadszedl
    deferredTimeout.current = setTimeout(flushDeferredResult, 1500)
    // przy "pending" popup sam sie nie zamyka (isNavigatingResult) - zamykamy go tutaj
    if (isNavigatingResult(result)) goBack()
  }

  useEffect(() => {
    if (!isPopupInHistory) flushDeferredResult()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPopupInHistory])

  useEffect(() => () => clearTimeout(deferredTimeout.current), [])

  const { t } = useTranslation('payment')
  const validateForm = () => {
    if (selected.methodId) {
      if (warning) setWarning(false)
      purchase()
    } else {
      setWarning(true)
    }
  }

  useEffect(() => {
    if (selected.methodId) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [selected.methodId])

  return (
    <>
      {popup && <div>
        {popup.type === 'blik'
          ? (() => {
            let localPaymentId = ''
            return <BlikPopup
              onResult={result => {
                onPopupResult(result, localPaymentId)
              }}
              getBody={extra => {
                localPaymentId = v7()
                return generateBody(localPaymentId, extra)
              }}
            />
          })()
          : popup.type === 'card'
            ? (() => {
              let localPaymentId = ''
              return <CardPopup
                cardForm={popup.cardForm || selected.cardForm}
                saveForRecurring={popup.saveForRecurring}
                onResult={result => {
                  onPopupResult(result, localPaymentId)
                }}
                getBody={extra => {
                  localPaymentId = v7()
                  return generateBody(localPaymentId, extra)
                }}
              />
            })()
            : null}
      </div>}

      <div className={`payment${secondaryTheme ? ' --secondary' : ''}`}>
        <div className='title'>
          <div>{t('payment')}</div>
          <div>{t('chooseOneOfOptions')}</div>
        </div>

        <div className='payment__main center'>
          <div>
            <div className='payment__summary center'>
              <div>
                <div className={`summary__item --primary${(summaryTop || summaryBottom) ? '' : ' --only'}`}>
                  <div>{t('toPay')}</div>
                  <div>{displayPrice(grossPrice)}</div>
                </div>
              </div>

              {(summaryTop || summaryBottom) && <div>
                <div className='summary__item'>
                  {summaryTop && <div>{summaryTop}</div>}
                  {summaryBottom && <div>{summaryBottom}</div>}
                </div>
              </div>}
            </div>

            {additionalElement}

            {bankTransfer
              ? <>
                <div className='payment__transfer'>
                  <div className='payment__transfer__label'>{t('recipientName')}</div>
                  <div className='payment__transfer__value'>
                    <span>{bankTransfer?.receiverName}</span>
                    <Copy text={bankTransfer?.receiverName || ''} />
                  </div>
                  <div className='payment__transfer__label'>{t('accountNumber')}</div>
                  <div className='payment__transfer__value'>
                    <span>{bankTransfer?.bankAccNumber}</span>
                    <Copy text={bankTransfer?.bankAccNumber || ''} />
                  </div>
                  <div className='payment__transfer__label'>{t('transferTitle')}</div>
                  <div className='payment__transfer__value'>
                    <span>{bankTransfer?.paymentTitle}</span>
                    <Copy text={bankTransfer?.paymentTitle || ''} />
                  </div>
                  {bankTransfer?.paymentDeadline && <>
                    <div className='payment__transfer__label'>{t('dueDate')}</div>
                    <div className='payment__transfer__value'>{bankTransfer?.paymentDeadline}</div>
                  </>}
                </div>

                <ArrowBtn
                  enlarged
                  text={t('finish')}
                  isLoading={isProcessing}
                  action={() => {
                    close('bank_transfer')
                  }} />
                <BackBtn
                  text={t('payOnline')}
                  action={clearBankTransfer}
                />
              </>
              : <>
                <p>{t('methodsAvailable')}</p>
                {warning && <p className='--warning'>{t('youMustChooseAPaymentMethod')}</p>}
                <div
                  className='payment__methods'
                  style={isProcessing ? { opacity: 0.5 } : undefined}>
                  {paymentMethods
                    ? paymentMethods.map((payment) => {
                      const { id, name, isBlocked } = payment
                      const isSelected = selected.methodId === id
                      const icon = getPaymentIcon(payment)

                      const additionalContent = []
                      if (isSelected && payment.supportsRecurring) {
                        additionalContent.push(<RecurringConsent
                          key='recurring'
                          name={`recurring-${id}`}
                          checked={!!selected.saveForRecurring}
                          disabled={isProcessing}
                          onGateway={payment.key === 'online'}
                          onChange={checked => select(prev => ({ ...prev, saveForRecurring: checked }))}
                        />)
                      }
                      if (isSelected && isWalletRedirect(payment)) {
                        additionalContent.push(<p
                          key='walletHint'
                          className='payment__hint'>
                          {t(payment.walletForm.wallet === 'gpay' ? 'chooseGooglePayOnGateway' : 'chooseApplePayOnGateway')}
                        </p>)
                      }

                      return <Option
                        key={id}
                        icon={icon}
                        title={name}
                        desc={getMethodDescription(payment)}
                        selected={isSelected}
                        additionalContent={additionalContent.length > 0 ? additionalContent : undefined}
                        action={() => {
                          if (isBlocked || isProcessing) return
                          select({ ...payment, methodId: id, saveForRecurring: false })
                        }} />
                    })
                    : isLoading
                      ? <Loading />
                      : <Error />}
                </div>

                <ArrowBtn
                  enlarged
                  isLoading={isProcessing}
                  text={t('payNow')}
                  action={validateForm} />

                <BackBtn
                  action={() => { close() }}
                />
                <div ref={bottomRef} />
              </>}
          </div>
        </div>
      </div>
    </>
  )
}

export default Payment
